import { get, type Writable } from 'svelte/store';
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import { aggregateTariffBuckets } from './energy_tariff_helpers';
import { buildDemoEnergyMonitoringStats } from './energy_demo_providers';
import { fetchEnergyStatistics } from './fetch_energy_statistics';
import { fetchPvGenerationChart } from './fetch_pv_energy_history';
import type { EnergyMonitoringPeriodId } from './period_helpers';
import { energyMonitoringDataMode, energyMonitoringStats, type EnergyMonitoringStats } from './store';

export const createEnergyMonitoringStatsLoader = (
	target: Writable<EnergyMonitoringStats> = energyMonitoringStats,
) => {
	let loadToken = 0;

	const load = async (controls: ChartControlsDataInterface, periodId: EnergyMonitoringPeriodId) => {
		const token = ++loadToken;
		target.update((state) => ({ ...state, loading: true, error: false }));

		try {
			if (get(energyMonitoringDataMode) === 'demo') {
				if (token !== loadToken) return;
				target.set({ ...buildDemoEnergyMonitoringStats(), error: false });
				return;
			}

			const [result, pvGenerationChart] = await Promise.all([
				fetchEnergyStatistics(controls, periodId),
				fetchPvGenerationChart(controls).catch(() => null),
			]);
			if (token !== loadToken) return;

			target.set({
				loading: false,
				error: false,
				chartData: pvGenerationChart ?? result?.chartData ?? null,
				statistic: result?.statistic ?? null,
				tariffBuckets: aggregateTariffBuckets(result?.history ?? []),
			});
		} catch {
			if (token !== loadToken) return;
			target.set({
				loading: false,
				error: true,
				chartData: null,
				statistic: null,
				tariffBuckets: [],
			});
		}
	};

	return {
		load,
		destroy: () => {
			loadToken += 1;
		},
	};
};
